import ActionMenu from '@components/Common/ActionMenu.js';
import { IS_MOBILE } from '@constants/global.js';
import { GripIcon } from '@icons/IconLibrary.js';
import { projectListReOrder } from '@signals/project/index.js';
import ProjectsState from '@states/Projects.js';
import { MemberState } from '@states/Viewer.js';
import { UIDiv, UIText } from '@ui';
import { getVisibleColumns } from '@utils/columnUtils.js';
import CheckboxCell from './cells/CheckboxCell.js';
import DateCell from './cells/DateCell.js';
import LinkCell from './cells/LinkCell.js';
import NumberCell from './cells/NumberCell.js';
import PeopleCell from './cells/PeopleCell.js';
import SelectCell from './cells/SelectCell.js';
import TextCell from './cells/TextCell.js';

export default class ProjectListViewRow extends UIDiv {
  constructor(
    item,
    isTask,
    canCreate,
    canUpdate,
    canShare,
    canDelete,
    canMove,
    canDownload,
    currentFolderId = null,
    selectedProjectType = null,
    isAnonymousView,
  ) {
    super();

    this.item = item;
    this.isTask = isTask;
    this.canUpdate = canUpdate;
    this.canCreate = canCreate;
    this.canShare = canShare;
    this.canDelete = canDelete;
    this.canMove = canMove;
    this.canDownload = canDownload;
    this.currentFolderId = currentFolderId;
    this.visibleColumns = getVisibleColumns(this.currentFolderId);
    this.selectedProjectType = selectedProjectType;
    this.isAnonymousView = isAnonymousView;

    // Store references to cells for external access
    this.cells = new Map();
    this.nameCell = null; // Special reference to name column cell

    // Track editing state for drag prevention
    this.isAnyCellEditing = false;

    this.addClass('project-list-view-row flex w-full min-w-fit cursor-pointer');
    this.dataset('projectListViewRowId', this.item.id);

    const urlParams = new URLSearchParams(window.location.search);
    const pId = urlParams.get('taskId');
    if (pId && this.item.id === parseInt(pId, 10)) {
      this.addClass('bg-yellow-50');
    }

    // Add drag and drop functionality if user can update

    this.render();
    if (this.canUpdate) this.setupRowDragEvents();
  }

  render() {
    // Create cells for each visible column
    this.visibleColumns.forEach(column => {
      // do not show 'Assignees' column if the user is not in an organization project page
      const { rootFolderOrganizationId } = ProjectsState.projects;
      if (!rootFolderOrganizationId && column.type === 'people') {
        return;
      }
      const cell = this.createCell(column);

      // Store cell reference by column key
      this.cells.set(column.key, cell);

      // Special handling for name column
      if (column.key === 'name' && !column.isCustom) {
        this.nameCell = cell;
      }
      this.add(cell);
    });

    if (this.canCreate) {
      // Add a 50px wide cell for the add column button (if can create)
      const addColumnCell = new UIDiv();
      addColumnCell.addClass('p-2 flex-shrink-0 border-b border-gray-200');
      addColumnCell.setStyle('width', ['50px']);
      this.add(addColumnCell);
    }

    // Add a spacer cell to fill remaining space
    const spacerCell = new UIDiv();
    spacerCell.addClass('flex-1 border-b border-gray-200');
    spacerCell.setStyle('min-width', ['0']);
    spacerCell.setStyle('max-width', ['none']);
    this.add(spacerCell);

    // Add actions column (always present)
    const actionsCell = this.createActionsCell();
    this.add(actionsCell);
  }

  createCell(column) {
    const cellType = this.createCellType(column);

    cellType.addClass('p-2 flex-shrink-0 border-b');
    if (!this.isAnonymousView) cellType.addClass('border-r border-gray-200');

    // Set all width constraints to prevent automatic resizing
    const width = this.isAnonymousView ? '250px' : column.width || '150px';
    cellType.setStyle('width', [width]);
    cellType.setStyle('min-width', [width]);
    cellType.setStyle('max-width', [width]);

    return cellType;
  }

  createCellType(column) {
    const { type, key, isCustom } = column;
    let value;
    let dragHandler = null;
    if (key === 'name' && !isCustom) {
      value = this.item.name || '';
      dragHandler = container => this.createDragHandle(container);
    } else if (key === 'createdAt' && !isCustom) {
      value = this.item.createdAt || '';
    } else if (this.item.columnValues) {
      const columnValue = this.item.columnValues.find(
        cv => cv.columnId === column.id || cv.columnId === column.key,
      );
      value = columnValue ? columnValue.value : '';
    } else {
      value = '';
    }

    // Handle standard columns
    switch (type) {
      case 'text':
        return new TextCell({
          column: column,
          item: this.item,
          value: value,
          canEdit: this.canUpdate,
          isTask: this.isTask,
          addDragHandle: dragHandler,
          setCellEditingState: bool => this.setCellEditingState(bool),
        });
      case 'date':
        return new DateCell({
          column: column,
          item: this.item,
          value: value,
          canEdit: this.canUpdate,
        });
      case 'select':
        return new SelectCell({
          column: column,
          item: this.item,
          value: value,
          canEdit: this.canUpdate,
          currentFolderId: this.currentFolderId,
        });
      case 'number':
        return new NumberCell(
          column,
          this.item,
          value,
          this.canUpdate,
          null,
          bool => this.setCellEditingState(bool),
        );
      case 'people':
        return new PeopleCell({
          column: column,
          item: this.item,
          value: value,
          canEdit: this.canUpdate,
          members: MemberState.members,
        });
      case 'checkbox':
        return new CheckboxCell(column, this.item, value, this.canUpdate);
      case 'link':
        return new LinkCell(
          column,
          this.item,
          value,
          this.canUpdate,
          null,
          bool => this.setCellEditingState(bool),
        );
      default:
        return new UIText('N/A');
    }
  }

  createActionsCell() {
    const cell = new UIDiv();
    cell.addClass(
      'border-b border-l border-gray-200 sticky right-0 flex-shrink-0 bg-white flex items-center justify-center z-10',
    );
    cell.setStyle('width', ['70px']);
    cell.setStyle('min-width', ['70px']);
    cell.setStyle('max-width', ['70px']);

    // Create popover menu trigger
    const menuTrigger = new ActionMenu({
      item: this.item,
      isTask: this.isTask,
      currentFolderId: this.currentFolderId,
      selectedProjectType: this.selectedProjectType,
      triggerRename: () => {
        // Trigger edit mode on the name column cell
        this.triggerNameEdit();
      },
      canCreate: this.canCreate,
      canShare: this.canShare,
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      canMove: this.canMove,
      canDownload: this.canDownload,
    });

    cell.add(menuTrigger);
    return cell;
  }

  /**
   * Create a drag handle for the row
   */
  createDragHandle(container) {
    // Check if this component has been destroyed
    if (!this.dom) {
      console.warn(
        'ProjectListViewRow: Cannot create drag handle - component has been destroyed',
      );
      return;
    }

    // Set draggable property only for non-mobile devices
    if (!IS_MOBILE()) {
      this.dom.draggable = true;
    }
    this.addClass('group/row');
    // Grip handle (visible on hover on desktop, always visible on mobile)
    const gripHandle = new UIDiv().setClass(
      `grip-handle flex-shrink-0 ${
        IS_MOBILE() ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'
      } cursor-move transition-opacity text-gray-400 z-20`,
    );
    const gripIcon = new GripIcon({
      size: 16,
      color: 'none',
    });
    gripHandle.add(gripIcon);

    // Store reference to grip handle for touch events
    this.gripHandle = gripHandle;

    container.add(gripHandle);
  }

  /**
   * Trigger edit mode on the name column cell
   */
  triggerNameEdit() {
    if (this.nameCell && typeof this.nameCell.enterEditMode === 'function') {
      this.nameCell.enterEditMode();
    }
  }

  /**
   * Get a cell by column key
   */
  getCell(columnKey) {
    return this.cells.get(columnKey);
  }

  /**
   * Set the editing state for the row
   * Called by cells when they enter/exit edit mode
   */
  setCellEditingState(isEditing) {
    this.isAnyCellEditing = isEditing;
  }

  /**
   * Set up drag events for the row (based on SelectOptionsModal pattern)
   */
  setupRowDragEvents() {
    // Desktop drag handler
    const moveHandler = e => {
      // Prevent dragging if any cell is in edit mode
      if (this.isAnyCellEditing) {
        e.preventDefault();
        return false;
      }

      if (e.dataTransfer) {
        // Desktop drag
        e.dataTransfer.setData('text/plain', this.item.id);
        e.dataTransfer.effectAllowed = 'move';
        this.addClass('opacity-50');

        // Store additional data for the drop handler
        e.dataTransfer.setData(
          'application/x-row-data',
          JSON.stringify({
            itemId: this.item.id,
            isTask: this.isTask,
            currentFolderId: this.currentFolderId,
          }),
        );
      }
    };

    // Mobile drag fallback - only attach to grip handle
    if (IS_MOBILE() && this.gripHandle) {
      let touchDragging = false;

      this.gripHandle.onTouchStart(e => {
        // Prevent dragging if any cell is in edit mode
        if (this.isAnyCellEditing) return;

        if (e.touches && e.touches.length === 1) {
          touchDragging = true;
          this.addClass('opacity-50');
          // Store drag data on instance
          this._mobileDragData = {
            itemId: this.item.id,
            isTask: this.isTask,
            currentFolderId: this.currentFolderId,
            draggedItem: {
              rowOrder: this.item.rowOrder,
            },
          };
        }
      });

      this.gripHandle.onTouchMove(e => {
        if (!touchDragging || this.isAnyCellEditing || !this.dom) return;
        // Find row under finger
        const touch = e.touches[0];
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!el) return;
        // Find parent row
        const row = el.closest('.project-list-view-row');
        if (row && row !== this.dom) {
          // Move the dragged element visually
          const rect = row.getBoundingClientRect();
          const midpoint = rect.top + rect.height / 2;
          if (touch.clientY < midpoint) {
            row.parentNode.insertBefore(this.dom, row);
          } else {
            row.parentNode.insertBefore(this.dom, row.nextSibling);
          }
        }
      });

      this.gripHandle.onTouchEnd(e => {
        if (touchDragging) {
          this.removeClass('opacity-50');
          touchDragging = false;
          // Only trigger reorder if not in edit mode
          if (!this.isAnyCellEditing) {
            const { itemId, isTask, currentFolderId } = this._mobileDragData;
            projectListReOrder.dispatch({
              itemId,
              isTask,
              currentFolderId,
            });
          }
          this._mobileDragData = null;
        }
      });
    }

    // Desktop drag events
    this.onDragStart(e => {
      return moveHandler(e);
    });

    this.onDragEnd(() => {
      this.removeClass('opacity-50');
    });

    this.onDragOver(e => {
      // Prevent drag over if any cell is editing or component is destroyed
      if (this.isAnyCellEditing || !this.dom) return;

      e.preventDefault();
      const draggingElement = document.querySelector(
        '.project-list-view-row.opacity-50',
      );
      if (draggingElement && draggingElement !== this.dom) {
        const rect = this.dom.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;

        if (e.clientY < midpoint) {
          this.dom.parentNode.insertBefore(draggingElement, this.dom);
        } else {
          this.dom.parentNode.insertBefore(
            draggingElement,
            this.dom.nextSibling,
          );
        }
      }
    });
  }

  /**
   * Cleanup method for row destruction
   * This helps prevent memory leaks when rows are removed
   */
  destroy() {
    // Clear all references to help with garbage collection
    this.item = null;
    this.visibleColumns = null;
    this.selectedProjectType = null;
    this.currentFolderId = null;
    this.isTask = null;
    this.canUpdate = null;
    this.canCreate = null;
    this.gripHandle = null;
    this.isAnyCellEditing = null;

    super.destroy();
  }
}
